import {computed, inject, Injectable, Injector, Signal, signal, untracked, WritableSignal} from "@angular/core";
import {EntityData, EntityModel, FedeltaImageData, field, identifier} from "@fedelta-pos/data-client";
import {SellableSizesData} from "./sellable-sizes-search.model";
import {MenuModel} from './menu.model';

export class ScheduleData extends EntityData<ScheduleData> {
    @identifier id: number;
    @field name: string;
}

export class MergedScheduleData {
    itemSchedules: ScheduleData[];
    exclItemSchedules: ScheduleData[];
    categorySchedules: ScheduleData[];
    exclCategorySchedules: ScheduleData[];
    primaryCategorySchedules: ScheduleData[];
    exclPrimaryCategorySchedules: ScheduleData[];
    submenuSchedules: ScheduleData[];
    exclSubmenuSchedules: ScheduleData[];
}

export class OrderTypeInfoData {
    orderType: "APP_AT_TABLE" | "TAKEAWAY" | "DELIVERY";
}

export class MenuSellableReferenceData {
    sellableId: number;
    name: string;
    integratedDisplayName: string;
    integratedProductImage: FedeltaImageData;
}

export interface MenuItem {
    id: number;
    name: string;
    description: string;
    hidden: boolean;
    orderTypes: OrderTypeInfoData[];
    exclOrderTypes: OrderTypeInfoData[];
    filterTags: string;
    sellableSizeGroup: number;
    productImage: FedeltaImageData;
    sellable: MenuSellableReferenceData | MenuItemSellableData;
    mergedSchedules: MergedScheduleData[];
    stores: number[];
    exclStores: number[];
    membershipTypeIds: number[];
    includeGuests: boolean;
    exclMembershipTypeIds: number[];
    excludeGuests: boolean;
}

export enum MenuItemImageOverlay {
    REMOVE = "REMOVE"
}

export enum MetaTagDefinitionType {
    // noinspection JSUnusedGlobalSymbols
    PRODUCT = "PRODUCT"
}

export class MenuExtraItemsMenuItemData implements MenuItem {
    id: number;
    name: string;
    description: string;
    hidden: boolean;
    orderTypes: OrderTypeInfoData[];
    exclOrderTypes: OrderTypeInfoData[];
    filterTags: string;
    includeOptionOnUntaggedItems: boolean = true;
    tagItemOnOptionSelection: boolean;
    sellableSizeGroup: number;
    productImage: FedeltaImageData;
    productImageOverlay: MenuItemImageOverlay;
    sellable: MenuSellableReferenceData;
    mergedSchedules: MergedScheduleData[];
    stores: number[];
    exclStores: number[];
    membershipTypeIds: number[];
    includeGuests: boolean;
    exclMembershipTypeIds: number[];
    excludeGuests: boolean;
}

export class MenuPlgItemsMenuItemData implements MenuItem {
    id: number;
    name: string;
    description: string;
    hidden: boolean;
    orderTypes: OrderTypeInfoData[];
    exclOrderTypes: OrderTypeInfoData[];
    // noinspection JSUnusedGlobalSymbols
    filterExtras: boolean;
    filterTags: string;
    includeOptionOnUntaggedItems: boolean = true;
    // noinspection JSUnusedGlobalSymbols
    tagItemOnOptionSelection: boolean;
    sellableSizeGroup: number;
    productImage: FedeltaImageData;
    sellable: MenuSellableReferenceData;
    mergedSchedules: MergedScheduleData[];
    stores: number[];
    exclStores: number[];
    membershipTypeIds: number[];
    includeGuests: boolean;
    exclMembershipTypeIds: number[];
    excludeGuests: boolean;
}

export class MenuDietaryIndicatorsData {
    id: number;
    code: string;
    name: string;
}

export class MenuSellableCustomObjectData {
    // noinspection JSUnusedGlobalSymbols
    customObjectId: number;
    name: string;
    description: string;
    // noinspection JSUnusedGlobalSymbols
    customObjectTypeId: number;
}

export class MenuSellableCategoryData {
    // noinspection JSUnusedGlobalSymbols
    categoryId: number;
    name: string;
}

export class MenuSellableCategoryLookupData {
    ancestorId: number;
    category: MenuSellableCategoryData;
}

export class MenuSellablePrimaryCategoryData {
    categoryId: number;
    name: string;
    ancestors: MenuSellableCategoryLookupData[];
}

export class MenuSellableMetaTagDefinitionData {
    // noinspection JSUnusedGlobalSymbols
    metaTagDefinitionId: number;
    name: string;
    type: MetaTagDefinitionType;
}

export class MenuExtraProductLineData {
    sellableId: number;
    name: string;
    // noinspection JSUnusedGlobalSymbols
    kiloJoules: string;
    suppressForDecomposedProductLine: boolean = false;
    // noinspection JSUnusedGlobalSymbols
    externalDescription: string;
    // noinspection JSUnusedGlobalSymbols
    integratedExternalDescription: string;
    integratedDisplayName: string;
    integratedProductImage: FedeltaImageData;
    // noinspection JSUnusedGlobalSymbols
    primaryCategory: MenuSellablePrimaryCategoryData;
    // noinspection JSUnusedGlobalSymbols
    dietaryIndicators: MenuDietaryIndicatorsData[];
    // noinspection JSUnusedGlobalSymbols
    customObjects: MenuSellableCustomObjectData[];
    tags: MenuSellableMetaTagDefinitionData[];
    // noinspection JSUnusedGlobalSymbols
    standardWaitTimeMinutes: number;
    // noinspection JSUnusedGlobalSymbols
    currentWaitTimeMinutes: number;
}

export class MenuProductExtraUsage {
    productLineId: number;
    parentExtraId: number;
}

export class MenuExtraProductData {
    id: string;
    price: number;
    qtyLimit: number;
    defaultOrderQty: number;
    // noinspection JSUnusedGlobalSymbols
    includeNestedExtras: boolean;
    productLine: MenuExtraProductLineData;

    /**
     * Assigned from the model to reference the respective menu item
     */
    menuItem: MenuExtraItemsMenuItemData;
}

export class MenuExtraGroupData {
    id: number;
    name: string;
    externalName: string;
    selectionMessage: string;
    promoted: boolean;
    promotedForWaiter: boolean;
    promotionMessage: string;
    groupQtyMin: number;
    groupQtyLimit: number;
    integratedDisplayName: string;
    integratedMinQty: number;
    integratedMaxQty: number;
    totalIncluded: number;
    usages: MenuProductExtraUsage[];
    extraProducts: MenuExtraProductData[];
    parentExtraProductIds: string[];
}

export class MenuSellableData {
    sellableId: number;
    name: string;
    plu: string;
    kiloJoules: string;
    price: number;
    guestPrice: number;
    bonusPoints: number;
    minWebOrderQty: number;
    decomposedProductLine: boolean = false;
    externalDescription: string;
    applyRsa: boolean = false;
    reorderableInApp: boolean;
    // noinspection JSUnusedGlobalSymbols
    allowZeroDollarInApp: boolean = false;
    promotedSellableId: number;
    integratedExternalDescription: string;
    integratedDisplayName: string;
    integratedProductImage: FedeltaImageData;
    primaryCategory: MenuSellablePrimaryCategoryData;
    extraGroups: MenuExtraGroupData[];
    dietaryIndicators: MenuDietaryIndicatorsData[];
    customObjects: MenuSellableCustomObjectData[];
    tags: MenuSellableMetaTagDefinitionData[];
    standardWaitTimeMinutes: number;
    currentWaitTimeMinutes: number;

    /**
     * Assigned from the model to reference the respective menu.
     */
    menuItem: MenuItemData;

    defaultPrice: number;
    defaultGuestPrice: number;
    extrasInitialised: boolean;
}

export interface MenuQtyData {
    indexOrder: number;
}

export class MenuProductLineQtyData implements MenuQtyData {
    indexOrder: number;
    productLine: MenuSellableData;
}

export class MenuPlgSellableData {
    defaultSellable: boolean;
    additionalPrice: number;
    sellable: MenuSellableData;

    /**
     * Assigned from the model to reference the respective menu item
     */
    menuItem: MenuPlgItemsMenuItemData;
}

export class MenuPlgData {
    plgId: number;
    name: string;
    externalName: string;
    sellableLines: MenuPlgSellableData[];
}

export class MenuProductLinePlgQtyData implements MenuQtyData {
    indexOrder: number;
    plg: MenuPlgData;
}

export class MenuCompositeData {
    sellableId: number;
    prodLineQuantities: MenuProductLineQtyData[];
    prodLinePlgQuantities: MenuProductLinePlgQtyData[];
}

export class MenuItemSellableData extends MenuSellableData {
    composite: MenuCompositeData;
}

export class MenuItemData implements MenuItem {
    id: number;
    name: string;
    description: string;
    pairingNotes: string;
    hidden: boolean;
    informational: boolean;
    orderTypes: OrderTypeInfoData[];
    exclOrderTypes: OrderTypeInfoData[];
    filterExtras: boolean;
    filterPlgItems: boolean;
    filterTags: string;
    includeOptionOnUntaggedItems: boolean = true;
    tagItemOnOptionSelection: boolean;
    sellableSizeGroup: number;
    courseId: number;

    // Not used here, but declared for compatibility with MenuPlgItemsMenuItemData to allow a menu item from a normal menu item to be used for a plg item.
    productImage: FedeltaImageData;

    sellable: MenuItemSellableData;

    mergedSchedules: MergedScheduleData[];
    stores: number[];
    exclStores: number[];
    membershipTypeIds: number[];
    includeGuests: boolean;
    exclMembershipTypeIds: number[];
    excludeGuests: boolean;

    // This is not part of the menu load, but is populated by the PromoModel.promotedFeatureTag computed signal as a side effect.
    // That computed signal is accessed from MenuItemPanel (reactively) and OrderItemOptionsPanel (non-reactively).
    // Other than in that computed signal, its only potentially reactive access is from OrderItemEditPage, where the intent is that
    // the feature tag would only be presented if it had previously been present on the menu (MenuItemPanel).
    // As such, there is no need for this to be reactive.
    promotedFeatureTag: string;

    // This is not part of the menu load, but is populated by the MenuModel.itemSellableSize computed signal as a side effect.
    // Since it's only access by that computed signal, it does not need to be reactive.
    sellableSize: SellableSizesData;

    hideWhenOrdered: boolean;

    // Lazily populated reactive state - populated by MenuModel
    state?: MenuItemState;
}

export class MenuItemState {
    initialised: WritableSignal<boolean> = signal(false);
    extrasInitialised: WritableSignal<boolean> = signal(false);
    private initialisationPending: boolean = false;

    public mergedItemForSellable: MenuItemData;

    public readonly isFullyInitialised: Signal<boolean> = computed(() => {
        if (this.initialised() && this.extrasInitialised()) {
            return true;
        }
        if (!this.initialisationPending) {
            this.initialisationPending = true;
            // The menu model enforces sequential lazy initialisation of menu items
            setTimeout(() => this.menuModel.initialiseMenuItem$.next(this.menuItem));
        }
        return false;
    });

    constructor(public readonly menuItem: MenuItemData,
                private readonly menuModel: MenuModel,
                private readonly menuEntityModel: MenuEntityModel) {
    }

    public initialise(initialiseExtras: boolean): void {
        untracked(() => {
            if (this.initialised() && (this.extrasInitialised() || !initialiseExtras)) {
                return;
            }

            if (this.menuModel.isCompositeItem(this.menuItem)) {
                this.menuModel.getCompositeLines(this.menuItem.sellable.composite).forEach((compositeLine: MenuQtyData) => {
                    if (this.menuModel.isProductLineQty(compositeLine)) {
                        const productLineQty: MenuProductLineQtyData = <MenuProductLineQtyData>compositeLine;
                        if (!this.initialised()) {
                            productLineQty.productLine.menuItem =
                                (<MenuItemData>this.menuModel.findAnyPlgItemsMenuItemForSellable()(productLineQty.productLine.sellableId)) ||
                                this.menuModel.findAnyMenuItemForSellable()(productLineQty.productLine.sellableId);
                        }
                        if (initialiseExtras) {
                            this.initialiseSellableExtras(productLineQty.productLine);
                        }
                    } else if (this.menuModel.isProductLinePlgQty(compositeLine)) {
                        const productLineQty: MenuProductLinePlgQtyData = <MenuProductLinePlgQtyData>compositeLine;
                        if (!this.initialised()) {
                            productLineQty.plg = this.menuEntityModel.dataSignal()?.plgs.find((plg: MenuPlgData) => plg.plgId === productLineQty.plg.plgId);
                        }
                        productLineQty.plg.sellableLines.forEach((sellableLine: MenuPlgSellableData) => {
                            if (initialiseExtras) {
                                this.initialiseSellableExtras(sellableLine.sellable);
                            }
                        });
                    }
                });
            } else if (initialiseExtras) {
                this.initialiseSellableExtras(this.menuItem.sellable);
            }

            // Calculate the default prices including extras
            const defaultExtrasPrice: number = this.menuItem.sellable.extrasInitialised ? this.menuModel.getDefaultExtrasPriceForSellable(this.menuItem.sellable) : 0;
            this.menuItem.sellable.defaultPrice = (this.menuItem.sellable.price + defaultExtrasPrice) * (this.menuItem.sellable.minWebOrderQty || 1);
            this.menuItem.sellable.defaultGuestPrice = (this.menuItem.sellable.guestPrice + defaultExtrasPrice) * (this.menuItem.sellable.minWebOrderQty || 1);

            this.initialised.set(true);
            this.extrasInitialised.set(initialiseExtras);
            this.initialisationPending = false;
        });
    }

    private initialiseSellableExtras(sellable: MenuSellableData): void {
        if (sellable.extrasInitialised) {
            return;
        }
        sellable.extraGroups = sellable.extraGroups.map((extraGroupRef: MenuExtraGroupData) =>
            this.menuEntityModel.dataSignal()?.extraGroups.find((extraGroup: MenuExtraGroupData) => extraGroup.id === extraGroupRef.id) ?? extraGroupRef);
        sellable.extraGroups.forEach((extraGroup: MenuExtraGroupData) => extraGroup.extraProducts ??= []);
        sellable.extrasInitialised = true;
    }
}


export class MenuCategoryData {
    id: number;
    name: string;
    description: string;
    integratedMenuCategoryDescription: string;
    categoryImage: FedeltaImageData;
    integratedMenuCategoryImage: FedeltaImageData;
    showWaiterItemImages: boolean;
    featured: boolean;
    hidden: boolean;
    informational: boolean;
    orderTypes: OrderTypeInfoData[];
    exclOrderTypes: OrderTypeInfoData[];
    mergedSchedules: MergedScheduleData[];
    stores: number[];
    // noinspection JSUnusedGlobalSymbols
    exclStores: number[];
    // noinspection JSUnusedGlobalSymbols
    membershipTypeIds: number[];
    // noinspection JSUnusedGlobalSymbols
    includeGuests: boolean;
    // noinspection JSUnusedGlobalSymbols
    exclMembershipTypeIds: number[];
    // noinspection JSUnusedGlobalSymbols
    excludeGuests: boolean;
    menuItems: MenuItemData[];
    // noinspection JSUnusedGlobalSymbols
    standardWaitTimeMinutes: number;
    currentWaitTimeMinutes: number;

    hasHideWhenOrderedItems: boolean;
    itemFeatureTag: string;

    pairingsCategory: boolean;
}

export class MenuPlgItemsData {
    id: number;
    name: string;
    menuItems: MenuPlgItemsMenuItemData[];
}

export class MenuExtraItemsData {
    id: number;
    name: string;
    menuItems: MenuExtraItemsMenuItemData[];
}

export class CountdownData {
    sellableId: number;
    qtyAvailable: number;
}

export class PairingProductData {
    sellableId: number;
    name: string;
}

export class PairingData {
    product: PairingProductData;
    notes: string;
    index: number;
    pairingSet: PairingSetData;
}

export class PairedItemData {
    product: PairingProductData;
    pairings: PairingData[];
}

export class PairingSetData {
    id: number;
    name: string;
    targetCategory: string;
    categoryTitle: string;
    categoryDescription: string;
    schedules: ScheduleData[];
    pairedItems: PairedItemData[];
}

export class MenuData extends EntityData<MenuData> {
    id: number;
    name: string;
    useSizeGroupNames: boolean;
    hideOutOfStock: boolean;
    defaultExtrasLimit: number;
    showDesktopItemImages: boolean;
    showDesktopCategoryImages: boolean;
    showDesktopSubcategoryImages: boolean;
    showMobileItemImages: boolean;
    showMobileCategoryImages: boolean;
    showMobileSubcategoryImages: boolean;
    showWaiterItemImages: boolean;
    showWaiterCategoryImages: boolean;
    showWaiterSubcategoryImages: boolean;
    defaultItemImage: FedeltaImageData;
    defaultCategoryImage: FedeltaImageData;
    pageImage: FedeltaImageData;
    pageImageOpacity: number;
    pageImageTextColor: string;
    pageImageBackgroundColor: string;
    categories: MenuCategoryData[];
    extraItems: MenuExtraItemsData[];
    plgItems: MenuPlgItemsData[];
    extraGroups: MenuExtraGroupData[];
    plgs: MenuPlgData[];
    countdowns: CountdownData[];
    pairingSets: PairingSetData[];
    rsaBlocked: boolean;
}

@Injectable()
export class MenuEntityModel extends EntityModel<MenuData> {
    constructor() {
        console.log("--- MenuEntityModel");
        super(inject(Injector), MenuData);
    }

    /**
     * For performance, serialization to EntityData has been disabled for this model
     */
    protected override getSerializeToEntityData(): boolean {
        return false;
    }
}
